import React, { useEffect } from 'react';
import { useToast, Text, HStack } from '@chakra-ui/react';
import { Button } from '@/kit/components/Button';
import { useNavigate } from 'react-router-dom';
import { PublicKey } from '@solana/web3.js';
import { useWallet } from '../../contexts/WalletContext';

declare global {
  interface Window {
    solana?: {
      isPhantom?: boolean;
      isConnected: boolean;
      publicKey: PublicKey | null;
      connect: (options?: { onlyIfTrusted?: boolean }) => Promise<{ publicKey: PublicKey }>;
      disconnect: () => Promise<void>;
      on: (event: string, callback: (args: any) => void) => void;
      removeListener: (event: string, callback: (args: any) => void) => void;
    };
    phantom?: {
      solana?: {
        isPhantom?: boolean;
        connect: (options?: any) => Promise<{ publicKey: PublicKey }>;
        disconnect: () => Promise<void>;
      }
    };
  }
}

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

export const WalletConnectButton = () => {
  const { publicKey, setPublicKey } = useWallet();
  const navigate = useNavigate();
  const toast = useToast();

  const handleMobileConnect = async () => {
    let isInIframe = false;

    try {
      isInIframe = window.self !== window.top;
    } catch (e) {
      isInIframe = true; // Ошибка значит, мы в iframe с другого домена
    }

    const dappUrl = window.location.href;
    const phantomUrl = `https://phantom.app/ul/browse/${encodeURIComponent(dappUrl)}?ref=${encodeURIComponent(dappUrl)}`;

    // window.location.href = phantomUrl;
    if (isInIframe) {
      window.parent.postMessage({ type: 'CLONER', payload: 'CONNECT' }, '*');
    } else {
      window.location.href = phantomUrl;
    }
  };

  const handleConnect = async () => {
    if (isMobile) {
      if (window.phantom?.solana?.isPhantom) {
        try {
          const connection = await window.phantom.solana.connect();
          setPublicKey(connection.publicKey);
          return;
        } catch (error) {
          console.error('Ошибка подключения через нативное расширение', error);
        }
      }
      handleMobileConnect();
      return;
    }

    if (!window.solana) {
      toast({
        title: "Phantom is not installed",
        description: "Please install the Phantom wallet",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      window.open('https://phantom.app/', '_blank');
      return;
    }

    try {
      const resp = await window.solana.connect();
      setPublicKey(resp.publicKey);
    } catch (err) {
      console.error("Ошибка подключения:", err);
    }
  };

  const handleDisconnect = async () => {
    if (window.solana) {
      await window.solana.disconnect();
      setPublicKey(null);
    } else if (window.phantom?.solana) {
      try {
        await window.phantom.solana.disconnect();
        setPublicKey(null);
      } catch (error) {
        console.error('Ошибка отключения от Phantom', error);
      }
    }
  };

  useEffect(() => {

    const handleMessage = (e: MessageEvent) => {
      console.log(e)
    }

    window.addEventListener('message', handleMessage);

    const initWallet = async () => {
      // Проверяем подключение через Phantom Mobileтзь

      if (window.phantom?.solana?.isPhantom) {
       
        try {
          const connection = await window.phantom.solana.connect({
            onlyIfTrusted: true
          });
          setPublicKey(connection.publicKey);
          return; // Если подключились через мобильный, прекращаем инициализацию
        } catch (error) {
          console.log('Не авторизован на мобильном устройстве', error);
        }
      }

      // Проверяем подключение через расширение
      if (window.solana?.isPhantom) {
        // Если уже подключен, используем текущее состояние
        if (window.solana.isConnected && window.solana.publicKey) {
          setPublicKey(window.solana.publicKey);
        } else {
          // Пробуем автоподключение только если не подключен
          try {
            const resp = await window.solana.connect({ onlyIfTrusted: true });
            setPublicKey(resp.publicKey);
          } catch (err) {
            console.log('Автоподключение не удалось', err);
          }
        }

        const handleConnect = (publicKey: PublicKey) => {
          setPublicKey(publicKey);
        };

        const handleDisconnect = () => {
          setPublicKey(null);
        };

        window.solana.on('connect', handleConnect);
        window.solana.on('disconnect', handleDisconnect);

        return () => {
          if (window.solana) {
            window.solana.removeListener('connect', handleConnect);
            window.solana.removeListener('disconnect', handleDisconnect);
          }
        };
      }
    };

    initWallet();

    // Проверяем, вернулись ли мы из Phantom mobile app с подключенным кошельком
    const checkRedirectFromPhantom = async () => {
      if (window.phantom?.solana) {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.has('phantom_encryption_public_key')) {
            const connection = await window.phantom.solana.connect();
            setPublicKey(connection.publicKey);
          }
        } catch (error) {
          console.error('Ошибка при обработке возврата из Phantom', error);
        }
      }
    };

    checkRedirectFromPhantom();
  }, [setPublicKey]);

  const shortenAddress = (address: string) => {
    if (address.length <= 8) return address;
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  return (
    <Button
      onClick={publicKey ? handleDisconnect : handleConnect}
    // colorScheme={publicKey ? "red" : "blue"}
    // variant={publicKey ? "outline" : "solid"}
    // size="md"
    // px={6}
    >
      {publicKey ? (
        <HStack spacing={2}>
          <Text>{shortenAddress(publicKey.toString())}</Text>
          <Text>×</Text>
        </HStack>
      ) : (
        "Connect Wallet"
      )}
    </Button>
  );
}; 